
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
	Alert,
	Box,
	Button,
	Container,
	Paper,
	TextField,
	Typography,
	createTheme,
	ThemeProvider,
} from '@mui/material';

import { AUTH } from '../config/api';
import * as ROUTES from '../config/routes';
import { genAxios, getErrorMessage } from '../services/api-services';

const fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';

const buttonSx = {
	margin: '27px 0px 0px',
	fontSize: '0.9375rem',
	lineHeight: '1.75',
	borderRadius: '10px',
	color: 'rgb(255, 255, 255)',
	backgroundColor: 'rgb(36, 66, 175)',
	boxShadow: 'none',
	fontWeight: 'bold',
	textTransform: 'none',
	padding: '11px 24px',
};

/**
 * Where the one-time link in a welcome or password-reset email lands: the user chooses a new password.
 */
const ResetPassword = () => {

	const mdTheme = createTheme();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState(null);
	const [done, setDone] = useState(false);

	const handleSubmit = () => {
		setError(null);
		if (!password) {
			setError('Please enter a new password.');
			return;
		}
		if (password !== confirmPassword) {
			setError('The passwords do not match.');
			return;
		}
		genAxios('POST', `${AUTH}/reset-password`, undefined, { token, password })
			.then(() => setDone(true))
			.catch(err => setError(getErrorMessage(err)));
	}

	return (

		<ThemeProvider theme={mdTheme}>

			<Box
				sx={{
					height: '100vh',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'rgb(248, 248, 248)'
				}}>

				<Container maxWidth='sm'>

					<Box sx={{ textAlign: 'center' }}>
						<img alt='' src='/img/transparent-navy.png' style={{ width: '30%' }} />
					</Box>

					<Paper
						sx={{
							color: 'rgb(34, 51, 84)',
							borderRadius: '10px',
							boxShadow: 'rgb(159 162 191 / 18%) 0px 9px 16px, rgb(159 162 191 / 32%) 0px 2px 2px',
							marginTop: '27px',
							padding: '36px',
						}}>

						<Typography
							component='h2'
							variant='h2'
							sx={{ margin: '0px 0px 9px', fontWeight: 700, fontSize: '30px', fontFamily, lineHeight: 1.2 }}>
							Set a new password
						</Typography>

						{ !token && <Alert severity='error' sx={{ mt: 3 }}>This link is incomplete. Please use the link from your email, or request a new one.</Alert> }

						{ token && done &&
							<React.Fragment>
								<Alert severity='success' sx={{ mt: 3 }}>Your password has been set. You can now sign in.</Alert>
								<Button href={ROUTES.SIGN_IN} size='large' fullWidth variant='contained' sx={buttonSx}>Sign in</Button>
							</React.Fragment>
						}

						{ token && !done &&
							<React.Fragment>
								<Typography sx={{ margin: '0px 0px 18px', fontSize: '16px', fontFamily, color: 'rgba(34, 51, 84, 0.7)' }}>
									Choose a new password for your account.
								</Typography>

								<form noValidate onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
									<TextField type='password' label='New Password' value={password} onChange={e => setPassword(e.target.value)} fullWidth margin='normal' variant='outlined' />
									<TextField type='password' label='Confirm New Password' value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} fullWidth margin='normal' variant='outlined' />
								</form>

								{ error && <Alert severity='error' sx={{ mt: 3 }}>{error}</Alert> }

								<Button size='large' fullWidth variant='contained' onClick={handleSubmit} sx={buttonSx}>Set password</Button>
							</React.Fragment>
						}

					</Paper>

					<Box sx={{ marginTop: '27px', textAlign: 'right' }}>
						<Typography
							component='a'
							href={ROUTES.FORGOT_PASSWORD}
							sx={{ fontSize: '15px', fontFamily, color: 'rgb(34, 51, 84)', fontWeight: 700 }}>
							<strong>Need a new link?</strong>
						</Typography>
					</Box>

				</Container>

			</Box>

		</ThemeProvider>

	);

}

export default ResetPassword;
